import { useCallback, useEffect, useState } from "react";
import {
  Wallet,
  Loader2,
  AlertTriangle,
  RefreshCcw,
  Clock,
} from "lucide-react";
import {
  obtenerSaldoSocio,
  tiempoRelativo,
  fmtMxn,
} from "@/data/saldoSocioStore";

/**
 * Tarjeta "Mi saldo" del Portal del Socio.
 *
 * Muestra únicamente:
 *   · Saldo total.
 *   · Desglose Cuotas / Consumos.
 *   · "Actualizado hace X horas" (a partir de `saldoActualizacion`).
 *
 * Reglas:
 *   · Si el saldo aún no está disponible (Casino Tampiqueño no lo ha entregado),
 *     mostramos "Pendiente / no disponible" — NUNCA $0.00 como si
 *     fuera un saldo confirmado.
 *   · Si `inconsistente=true` (backend detectó total ≠ cuotas +
 *     consumos), mostramos un aviso discreto para que el socio sepa
 *     que ese dato se revisa. La UI no calcula la suma por su cuenta:
 *     usamos lo que devuelve el backend.
 *   · NO existen historiales de pagos/cuotas/consumos en el portal.
 */
export default function SaldoCard() {
  const [saldo, setSaldo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const hidratar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const data = await obtenerSaldoSocio();
      setSaldo(data);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401 || s === 403) setError("Tu sesión expiró. Vuelve a iniciar sesión.");
      else setError("No pudimos obtener tu saldo. Intenta más tarde.");
      // El saldo previamente cargado se conserva: mostramos el error
      // como banner no-destructivo (ver render) en lugar de ocultarlo.
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    hidratar();
  }, [hidratar]);

  return (
    <section
      data-testid="socio-saldo-card"
      className="animate-fade-up delay-150"
    >
      <div className="relative overflow-hidden rounded-2xl border hairline bg-card p-6 md:p-8 shadow-soft">
        <div
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full opacity-50 dark:opacity-25 blur-3xl"
          style={{ background: "hsl(var(--accent) / 0.35)" }}
        />
        <div className="relative flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-accent/10 grid place-items-center">
              <Wallet
                className="w-5 h-5 text-primary dark:text-accent"
                strokeWidth={1.6}
              />
            </span>
            <div>
              <p className="overline text-accent">Mi saldo</p>
              <p className="text-sm text-muted-foreground">
                Información proporcionada por Casino Tampiqueño.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="socio-saldo-refrescar"
            onClick={hidratar}
            disabled={cargando}
            className="p-2 rounded-full text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
            aria-label="Actualizar saldo"
            title="Actualizar"
          >
            {cargando ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4" />
            )}
          </button>
        </div>

        {error && !saldo ? (
          <p
            data-testid="socio-saldo-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {error}
          </p>
        ) : cargando && !saldo ? (
          <div
            data-testid="socio-saldo-cargando"
            className="flex items-center gap-2 text-sm text-muted-foreground py-6"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando tu saldo…
          </div>
        ) : saldo && saldo.disponible ? (
          <>
            {error ? (
              <div
                data-testid="socio-saldo-error-banner"
                role="alert"
                className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2 text-xs"
              >
                {error}
              </div>
            ) : null}
            <SaldoDisponible saldo={saldo} />
          </>
        ) : (
          <SaldoPendiente mensaje={saldo?.mensaje} />
        )}
      </div>
    </section>
  );
}

function SaldoDisponible({ saldo }) {
  return (
    <div
      data-testid="socio-saldo-disponible"
      className="relative flex flex-col gap-6"
    >
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Saldo total
        </p>
        <p
          data-testid="socio-saldo-total"
          className="mt-1 font-heading text-4xl md:text-5xl tabular-nums tracking-tight text-foreground"
        >
          {fmtMxn(saldo.saldoTotal)}
        </p>
        <p
          data-testid="socio-saldo-actualizado"
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <Clock className="w-3.5 h-3.5" />
          Actualizado {tiempoRelativo(saldo.saldoActualizacion)}
        </p>
      </div>

      {saldo.inconsistente ? (
        <div
          data-testid="socio-saldo-inconsistente"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-2 text-xs flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            El total y el desglose que recibimos no coinciden. Ya lo
            estamos revisando con Administración; el saldo definitivo
            puede ajustarse en la próxima sincronización.
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <FilaDesglose
          label="Cuotas"
          value={fmtMxn(saldo.saldoCuotas)}
          testid="socio-saldo-cuotas"
        />
        <FilaDesglose
          label="Consumos"
          value={fmtMxn(saldo.saldoConsumos)}
          testid="socio-saldo-consumos"
        />
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Este saldo refleja el corte más reciente publicado por Casino
        Tampiqueño. Para ver movimientos detallados, acércate a
        Recepción o Administración.
      </p>
    </div>
  );
}

function SaldoPendiente({ mensaje }) {
  return (
    <div
      data-testid="socio-saldo-pendiente"
      className="relative flex flex-col gap-3 py-4"
    >
      <div className="inline-flex items-center gap-2 text-sm">
        <span className="inline-flex items-center rounded-full border hairline px-2.5 py-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
          Pendiente
        </span>
        <span className="text-muted-foreground">
          Saldo no disponible por ahora
        </span>
      </div>
      <p className="text-sm text-foreground/80">
        {mensaje ||
          "Tu saldo aún no está disponible. Consulta directamente en Recepción o inténtalo más tarde."}
      </p>
    </div>
  );
}

function FilaDesglose({ label, value, testid }) {
  return (
    <div className="rounded-xl border hairline bg-background/60 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        data-testid={testid}
        className="mt-1 font-heading text-lg tabular-nums text-foreground"
      >
        {value}
      </p>
    </div>
  );
}
